import { Box, IconButton, InputAdornment, TextField, Theme, Typography, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import BreadCrum from "../../../../lib/BreadCrum";
import { RootStateType } from "../../../../redux-store/reducers";
import { isModuleAllowed } from "../../../../utils/utilityFunctions";
import { useEffect, useRef, useState } from "react";
import { PaginationObj } from "../../../investorOverview/types";
import { GetRiaFeeUpdateAuditDataResponseBody, GetRiaFeeUpdateAuditDetailsResponseBody, RiaFeeUpdateAuditDataObj, RiaFeeUpdateAuditDetailsObj } from "../../../../redux-store/types/onBoarding";
import { getRiaFeeUpdateAuditData, getRiaFeeUpdateAuditDetails } from "../../../../redux-store/actions/reports";
import FeeSetupHistoryTable from "./FeeSetupHistoryTable";
import AuditDetailsPopup from "../../AuditDetailsPopup";
import { Form, Formik, FormikProps } from "formik";
import MFSelectField from "../../../../lib/formik/SelectField";
import { MFSubmitButton } from "../../../../lib/formik";
import { SearchIcon } from "../../../customSVGs";
import LoadingButton from "@mui/lab/LoadingButton";
import { DialogCloseIcon } from "../../../../assets/SvgIcons";

interface FilterValues {
    oldFeeType: string;
    newFeeType: string;
}

const initialFilterValues: FilterValues = {
    oldFeeType: "",
    newFeeType: ""
}

const FEE_TYPE_OPTIONS = [
    { key: "Fixed fee", value: "FIXED" },
    { key: "AUA Fee (Variable)", value: "VARIABLE" },
    { key: "AUA Fee (Slab Wise)", value: "SLAB" },
]

const FILTER_FIELD_HEIGHT = "56px";
const FILTER_ERROR_GAP = "6px";
const FILTER_ERROR_SLOT_HEIGHT = "22px";
const FILTER_COLUMN_HEIGHT = `calc(${FILTER_FIELD_HEIGHT} + ${FILTER_ERROR_GAP} + ${FILTER_ERROR_SLOT_HEIGHT})`;

const filterInputHeightSx = {
    "& .MuiInputBase-root": {
        height: FILTER_FIELD_HEIGHT,
        minHeight: FILTER_FIELD_HEIGHT,
        maxHeight: FILTER_FIELD_HEIGHT,
        boxSizing: "border-box",
    },
    "& .MuiSelect-select": {
        padding: "15px",
        height: "100%",
        minHeight: "unset",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
    },
};

const filterErrorSx = {
    position: "absolute",
    left: 0,
    top: `calc(${FILTER_FIELD_HEIGHT} + ${FILTER_ERROR_GAP})`,
    margin: 0,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
};

const filterFieldWrapperSx = {
    flex: { xs: "1 1 100%", lg: "0 1 auto" },
    minWidth: { xs: "100%", lg: "220px" },
    maxWidth: { xs: "100%", lg: "280px" },
    width: { xs: "100%", lg: "auto" },
    position: "relative",
    minHeight: FILTER_COLUMN_HEIGHT,
    "& .MuiFormControl-root": {
        my: 0,
        paddingBottom: 0,
    },
    "& .MuiFormHelperText-root": filterErrorSx,
    ...filterInputHeightSx,
};

const filterSubmitWrapperSx = {
    flex: { xs: "1 1 100%", lg: "0 0 auto" },
    position: "relative",
    minHeight: { xs: "auto", lg: FILTER_COLUMN_HEIGHT },
    display: "flex",
    alignItems: "flex-start",
    width: { xs: "100%", lg: "auto" },
    marginTop: { xs: "0", lg: 0 }
};

const filterSubmitButtonSx = (theme: Theme) => ({
    ...theme.typography.body_500,
    fontWeight: 600,
    height: { xs: "auto", lg: FILTER_FIELD_HEIGHT },
    minHeight: { xs: "42px", lg: FILTER_FIELD_HEIGHT },
    maxHeight: { xs: "48px", lg: FILTER_FIELD_HEIGHT },
    py: { xs: "6px", lg: 0 },
    px: { xs: "16px", lg: "20px" },
    lineHeight: 1.2,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
    width: { xs: "100%", lg: "auto" },
    minWidth: { xs: "unset", lg: "fit-content" },
    flexShrink: 0,
    "& .MuiCircularProgress-root": {
        color: theme.palette.text.primary,
    },
});

const DEBOUNCE_DELAY = 300

export default function FeeSetupHistory(): JSX.Element {
    const theme = useTheme()
    const dispatch = useDispatch()

    const history = useHistory();
    const { pathname: pathName } = history.location
    const initialRouter = pathName.split('/').filter((item) => item !== '')

    // Data from Redux
    const { permissions = [] } = useSelector((store: RootStateType) => store.auth);

    // State
    const [loadingStatus, setLoadingStatus] = useState<
        "tableDataLoading" |
        "appliedFilterLoading" |
        "clearFilterLoading" |
        ""
    >("")
    const [searchText, setSearchText] = useState<string>("")
    const [tableData, setTableData] = useState<Array<RiaFeeUpdateAuditDataObj>>([])
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0,
    })
    const [loadingRowId, setLoadingRowId] = useState<number | null>(null)
    const [auditDetailsPopupData, setAuditDetailsPopupData] = useState<{
        data: RiaFeeUpdateAuditDetailsObj | null;
        isOpen: boolean;
    }>({
        data: null,
        isOpen: false
    })

    const routerPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            textSx: { paddingLeft: "0px" }
        },
        {
            name: 'RIA Fee Setup',
            path: `/${initialRouter[0]}`,
            isTextLowerCase: false
        },
        {
            name: 'History',
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            isTextLowerCase: false
        }
    ] : [])

    const filterFormikRef = useRef<FormikProps<FilterValues>>(null);
    const lastAppliedFilterValuesRef = useRef<FilterValues | null>(null);

    // Prevents the debounced search useEffect from firing on initial mount.
    // On mount, searchText is "" and the effect runs, but this ref being false
    // ensures we skip it (the initial useEffect already fetches data).
    // Only set to true when the user actually interacts with the search input.
    const isSearchDirtyRef = useRef<boolean>(false);

    const fetchAuditData = async ({
        selectedCurrentPage,
        selectedRowsPerPage,
        selectedOldFeeType,
        selectedNewFeeType,
        searchInput = "",
        cancelledRef
    }: {
        selectedCurrentPage: number;
        selectedRowsPerPage: number;
        selectedOldFeeType: string;
        selectedNewFeeType: string;
        searchInput?: string;
        cancelledRef?: { current: boolean };
    }): Promise<boolean> => {

        const payload = {
            currentPage: selectedCurrentPage,
            rowsPerPage: selectedRowsPerPage,
            oldFeeType: selectedOldFeeType,
            newFeeType: selectedNewFeeType,
            searchInput
        }

        const response = await (dispatch(getRiaFeeUpdateAuditData(payload)) as unknown as Promise<GetRiaFeeUpdateAuditDataResponseBody>)

        if (cancelledRef?.current) {
            return false
        }

        if (!response) {
            setTableData([])
            setTablePaginationObj({
                currentPage: 0,
                rowsPerPage: 5,
                totalRows: 0
            });

            return false
        }

        setTableData(response?.data ?? [])
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: selectedCurrentPage - 1,
            rowsPerPage: selectedRowsPerPage,
            totalRows: response?.count ?? 0
        }))

        return true
    }

    const handleTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            if (!lastAppliedFilterValuesRef?.current) return

            setLoadingStatus("tableDataLoading")

            await fetchAuditData({
                selectedCurrentPage: 1,
                selectedRowsPerPage: Number(event.target.value),
                selectedOldFeeType: lastAppliedFilterValuesRef.current?.oldFeeType ?? "",
                selectedNewFeeType: lastAppliedFilterValuesRef.current?.newFeeType ?? "",
                searchInput: searchText
            })
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingStatus("")
        }
    };

    const handleTablePageChange = async (_event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        try {
            if (!lastAppliedFilterValuesRef?.current) return

            setLoadingStatus("tableDataLoading")

            await fetchAuditData({
                selectedCurrentPage: page + 1,
                selectedRowsPerPage: tablePaginationObj.rowsPerPage,
                selectedOldFeeType: lastAppliedFilterValuesRef.current?.oldFeeType ?? "",
                selectedNewFeeType: lastAppliedFilterValuesRef.current?.newFeeType ?? "",
                searchInput: searchText
            })
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingStatus("")
        }
    };

    const handleApplyFilters = async (values: FilterValues) => {
        try {
            setLoadingStatus("appliedFilterLoading")

            await fetchAuditData({
                selectedCurrentPage: 1,
                selectedRowsPerPage: tablePaginationObj.rowsPerPage,
                selectedOldFeeType: values?.oldFeeType ?? "",
                selectedNewFeeType: values?.newFeeType ?? "",
                searchInput: searchText
            })

            lastAppliedFilterValuesRef.current = { ...values }
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingStatus("")
        }
    };

    const handleViewDetailsClick = async (id: number) => {
        try {
            setLoadingRowId(id)

            const response = await (dispatch(getRiaFeeUpdateAuditDetails(id)) as unknown as Promise<GetRiaFeeUpdateAuditDetailsResponseBody>)

            if (response) {
                setAuditDetailsPopupData({
                    data: response?.data ?? null,
                    isOpen: true
                })
            }
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setLoadingRowId(null)
        }
    }

    const handleAuditDetailsPopupClose = () => {
        setAuditDetailsPopupData(prevState => ({
            ...prevState,
            isOpen: false
        }))
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        isSearchDirtyRef.current = true
        e.preventDefault()
    }

    const handleClearFilters = async () => {
        try {
            if (filterFormikRef?.current) {
                filterFormikRef.current.resetForm()
            }

            setLoadingStatus("clearFilterLoading")

            await fetchAuditData({
                selectedCurrentPage: 1,
                selectedRowsPerPage: tablePaginationObj.rowsPerPage,
                selectedOldFeeType: "",
                selectedNewFeeType: "",
                searchInput: searchText
            })

            lastAppliedFilterValuesRef.current = { oldFeeType: "", newFeeType: "" }
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingStatus("")
        }
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
        isSearchDirtyRef.current = true
    }

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setLoadingStatus("tableDataLoading")

                await fetchAuditData({
                    selectedCurrentPage: 1,
                    selectedRowsPerPage: tablePaginationObj.rowsPerPage,
                    selectedOldFeeType: "",
                    selectedNewFeeType: "",
                    searchInput: ""
                })

                lastAppliedFilterValuesRef.current = { oldFeeType: "", newFeeType: "" }
            } catch (error) {
                console.log(error)
            } finally {
                setLoadingStatus("")
            }
        }
        loadInitialData()
    }, [])

    useEffect(() => {
        if (!isSearchDirtyRef.current) {
            return
        }

        let cancelled = false
        const cancelledRef = { current: false }

        const debounceTimer = setTimeout(async () => {
            const appliedValues = lastAppliedFilterValuesRef.current

            setLoadingStatus("tableDataLoading")

            try {
                await fetchAuditData({
                    selectedCurrentPage: 1,
                    selectedRowsPerPage: tablePaginationObj.rowsPerPage,
                    selectedOldFeeType: appliedValues?.oldFeeType ?? "",
                    selectedNewFeeType: appliedValues?.newFeeType ?? "",
                    searchInput: searchText,
                    cancelledRef
                })
            } catch (error) {
                if (cancelled) return
                console.log(error)
            } finally {
                if (!cancelled) {
                    setLoadingStatus("")
                }
            }
        }, DEBOUNCE_DELAY)

        return () => {
            clearTimeout(debounceTimer)
            cancelled = true
            cancelledRef.current = true
        }
    }, [searchText])

    return (
        <>
            <Box sx={{ marginBottom: "1rem" }}>
                <BreadCrum array={routerPath} />
            </Box>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", lg: "row" },
                    alignItems: { xs: "flex-start", lg: "flex-start" },
                    justifyContent: { xs: "flex-start", lg: "space-between" },
                    gap: { xs: "1rem", lg: "1rem" },
                    marginBottom: "2rem",
                }}
            >
                <Typography
                    variant="h1"
                    sx={{
                        color: theme.palette.text.primary,
                        flexShrink: 0,
                        marginTop: "10px",
                    }}
                >
                    History
                </Typography>
                <Box
                    sx={{
                        width: { xs: "100%", lg: "auto" },
                        minWidth: 0,
                        flexShrink: 1,
                    }}
                >
                    <Formik
                        innerRef={filterFormikRef}
                        initialValues={initialFilterValues}
                        onSubmit={handleApplyFilters}
                    >
                        {() => (
                            <Form style={{ width: "100%", maxWidth: "100%" }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", lg: "row" },
                                        flexWrap: { xs: "nowrap", lg: "wrap" },
                                        alignItems: { xs: "stretch", lg: "flex-start" },
                                        justifyContent: 'flex-start',
                                        gap: { xs: "0.8rem", lg: "1rem" },
                                        width: "100%",
                                        maxWidth: "100%",
                                        minWidth: 0,
                                    }}
                                >
                                    <Box sx={filterFieldWrapperSx}>
                                        <MFSelectField
                                            name='oldFeeType'
                                            label="Previous Fee Type"
                                            items={FEE_TYPE_OPTIONS}
                                            disabled={[
                                                "tableDataLoading", "appliedFilterLoading", "clearFilterLoading"
                                            ].includes(loadingStatus)}
                                        />
                                    </Box>
                                    <Box sx={filterFieldWrapperSx}>
                                        <MFSelectField
                                            name='newFeeType'
                                            label="New Fee Type"
                                            items={FEE_TYPE_OPTIONS}
                                            disabled={[
                                                "tableDataLoading", "appliedFilterLoading", "clearFilterLoading"
                                            ].includes(loadingStatus)}
                                        />
                                    </Box>
                                    <Box sx={filterSubmitWrapperSx}>
                                        <MFSubmitButton
                                            label="Apply Filters"
                                            fullWidth={false}
                                            sx={filterSubmitButtonSx(theme)}
                                            loadingEnable={["appliedFilterLoading"].includes(loadingStatus)}
                                            disabled={[
                                                "tableDataLoading", "clearFilterLoading"
                                            ].includes(loadingStatus)}
                                            loadingPosition="center"
                                        />
                                    </Box>
                                    <Box sx={filterSubmitWrapperSx}>
                                        <LoadingButton
                                            type="button"
                                            variant="outlined"
                                            onClick={handleClearFilters}
                                            loading={loadingStatus === "clearFilterLoading"}
                                            loadingPosition="center"
                                            disabled={["tableDataLoading", "appliedFilterLoading"].includes(loadingStatus)}
                                            sx={{
                                                ...filterSubmitButtonSx(theme),
                                                borderColor: theme.palette.primary.main,
                                                borderRadius: "8px",
                                                color: theme.palette.primary.main,
                                                "&:hover": {
                                                    borderColor: theme.palette.primary.main,
                                                    backgroundColor: "transparent",
                                                },
                                                "& .MuiCircularProgress-root": {
                                                    color: theme.palette.primary.main,
                                                },
                                            }}
                                        >
                                            Clear
                                        </LoadingButton>
                                    </Box>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Box>
            <Box
                sx={{
                    padding: '25px',
                    borderRadius: '15px',
                    background: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                }}
            >
                <TextField
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="Clear search"
                                    sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                    onMouseDown={handleClearSearch}
                                >
                                    <DialogCloseIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius: '10px',
                            height: '50px',
                            border: `1px solid ${theme.palette.text.secondary}B2`,
                            '& fieldset': {
                                border: 'none',
                            },
                            '& input': {
                                paddingLeft: '10px',
                                ...theme.typography.body_500,
                                color: theme.palette.text.primary,
                                '::placeholder': {
                                    ...theme.typography.subtitle_400,
                                    color: theme.palette.text.secondary,
                                    opacity: 1,
                                },
                            },
                            marginBottom: "3rem"
                        },
                    }}
                    value={searchText}
                    placeholder="Search..."
                    onChange={handleSearchTextChange}
                />
                <FeeSetupHistoryTable
                    isLoading={[
                        "tableDataLoading",
                        "appliedFilterLoading",
                        "clearFilterLoading"
                    ].includes(loadingStatus)}
                    tableData={tableData}
                    tablePaginationObj={tablePaginationObj}
                    handleTableRowsPerPageChange={handleTableRowsPerPageChange}
                    handleTablePageChange={handleTablePageChange}
                    handleViewDetailsClick={handleViewDetailsClick}
                    loadingRowId={loadingRowId}
                />
            </Box>
            <AuditDetailsPopup
                open={auditDetailsPopupData.isOpen}
                onClose={handleAuditDetailsPopupClose}
                data={auditDetailsPopupData.data}
                comingFrom="feeSetupHistory"
            />
        </>
    )
}